import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, RefreshCw, CheckCircle, XCircle, AlertCircle,
  Link2, Calendar, Eye, EyeOff, Download,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { BrandGlyph } from './IntegrationsPage'
import type {
  TimeTrackerStatus, TimeTrackerUser, SyncLogEntry,
  CustomerProjectMappingResponse, MappingAvailableRow, MappingUnavailableRow,
  MappingUnavailableReason,
  UserProjectMappingResponse, UserMappingAvailableRow, UserMappingUnavailableRow,
  UserMappingUnavailableReason,
  ResourcePriceMappingResponse, PriceMappingAvailableRow, PriceMappingUnavailableRow,
  PriceMappingUnavailableReason,
  TimeMappingResponse, TimeMappingAvailableRow, TimeMappingUnavailableRow,
  TimeMappingUnavailableReason,
} from '@/types/api'

const GLYPH = {
  monogram: 'TT',
  gradient: 'linear-gradient(135deg, oklch(0.68 0.18 55) 0%, oklch(0.55 0.19 40) 100%)',
}

type Tab = 'users' | 'log' | 'customer-mapping' | 'user-mapping' | 'price-mapping' | 'time-mapping' | 'mapping'

export default function TimeTrackerPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('users')

  const [server,   setServer]   = useState('')
  const [database, setDatabase] = useState('timeregistration-prod')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd,  setShowPwd]  = useState(false)
  const [search,   setSearch]   = useState('')

  const { data: status, isLoading } = useQuery({
    queryKey: ['timetracker', 'status'],
    queryFn:  () => api.get<TimeTrackerStatus>('/timetracker/status'),
  })
  const { data: users = [] } = useQuery({
    queryKey: ['timetracker', 'users-list'],
    queryFn:  () => api.get<TimeTrackerUser[]>('/timetracker/users'),
    enabled:  status?.connected === true,
  })
  const { data: syncLog = [] } = useQuery({
    queryKey: ['timetracker', 'sync-log'],
    queryFn:  () => api.get<SyncLogEntry[]>('/timetracker/sync-log'),
    enabled:  status?.connected === true,
  })

  const configureMutation = useMutation({
    mutationFn: () => api.post<unknown>('/timetracker/configure', { server, database, username, password }),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['timetracker'] }),
  })
  const disconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/timetracker/connect'),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['timetracker'] }),
  })
  const syncMutation = useMutation({
    mutationFn: () => api.post<unknown>('/timetracker/sync', {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['timetracker'] }),
  })

  const handleDisconnect = () => {
    if (!confirm('Disconnect TimeTracker? The sync will stop and credentials will be deleted.')) return
    disconnectMutation.mutate()
  }

  if (isLoading) {
    return <div className="page"><Breadcrumb /><p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p></div>
  }

  // ── Not connected ────────────────────────────────────────────────────────────
  if (!status?.connected) {
    return (
      <div className="page">
        <Breadcrumb />
        <div className="card" style={{ maxWidth: 520, padding: 32 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
            <BrandGlyph {...GLYPH} size={48} />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Connect TimeTracker</h2>
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                Sync historical time data during transition to Forge time tracking.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            <Field label="Server">
              <input className="input" placeholder="server.database.windows.net"
                value={server} onChange={e => setServer(e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Database">
              <input className="input" placeholder="timeregistration-prod"
                value={database} onChange={e => setDatabase(e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Username">
              <input className="input" placeholder="SQL username"
                value={username} onChange={e => setUsername(e.target.value)} style={{ width: '100%' }} />
            </Field>
            <Field label="Password">
              <div style={{ position: 'relative' }}>
                <input className="input" type={showPwd ? 'text' : 'password'}
                  placeholder="SQL password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  style={{ width: '100%', paddingRight: 40, fontFamily: 'JetBrains Mono', fontSize: 13 }} />
                <button className="btn btn-ghost btn-icon btn-sm" type="button"
                  onClick={() => setShowPwd(v => !v)}
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}>
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
          </div>

          <button className="btn btn-brand"
            style={{ width: '100%', justifyContent: 'center', height: 40, gap: 8 }}
            disabled={!server || !database || !username || !password || configureMutation.isPending}
            onClick={() => configureMutation.mutate()}>
            {configureMutation.isPending
              ? <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />Connecting…</>
              : 'Connect TimeTracker'
            }
          </button>
          {configureMutation.isError && (
            <p style={{ color: 'var(--ns-err)', fontSize: 12.5, marginTop: 10 }}>
              Connection failed — check credentials and try again.
            </p>
          )}
        </div>
      </div>
    )
  }

  // ── Connected ────────────────────────────────────────────────────────────────
  const lastSyncDate = status.lastSyncAt
    ? new Date(status.lastSyncAt).toLocaleDateString('sv-SE')
    : null
  const lastSyncTime = status.lastSyncAt
    ? new Date(status.lastSyncAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
    : null

  const filteredUsers = users.filter(u =>
    `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase().includes(search.toLowerCase()) ||
    (u.email ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero header */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, oklch(0.68 0.18 55 / 0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>TimeTracker</h1>
              <span className="badge badge-ok"><span className="dot dot-ok" />Connected</span>
              <span className="muted" style={{ fontSize: 12 }}>· Transition sync — until cutover</span>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Historical time entries, projects and users synced nightly at 03:00.
            </p>
            <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
              <span className="row" style={{ gap: 5 }}><Link2 size={12} />{status.server}</span>
              <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
              <span className="mono" style={{ fontSize: 11 }}>{status.database}</span>
              {lastSyncDate && <>
                <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
                <span className="row" style={{ gap: 5 }}><Calendar size={12} />Synced {lastSyncDate}</span>
              </>}
            </div>
          </div>
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 5, color: 'var(--ns-err)' }}
              onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
              <Link2 size={13} />Disconnect
            </button>
            <button className="btn btn-brand btn-sm" style={{ gap: 5 }}
              onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              <RefreshCw size={13} style={{ animation: syncMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
              {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
        <StatCard label="Users"        value={status.userCount      ?? 0} />
        <StatCard label="Customers"    value={status.customerCount  ?? 0} />
        <StatCard label="Projects"     value={status.projectCount   ?? 0} />
        <StatCard label="Time entries" value={status.timeEntryCount ?? 0} />
        <StatCard label="Last sync" isCustom
          value={lastSyncTime
            ? <span className="mono tabular" style={{ fontSize: 20, fontWeight: 600 }}>{lastSyncTime}</span>
            : <span className="muted" style={{ fontSize: 15 }}>Never</span>}
          sub={lastSyncDate ?? undefined}
        />
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {([
          ['users',            'Users',                    users.length],
          ['customer-mapping', 'Customer Project Mapping', null],
          ['user-mapping',     'User Project Mapping',     null],
          ['price-mapping',    'Resource Pricing',         null],
          ['time-mapping',     'Registered Time',          null],
          ['log',              'Sync log',                 null],
          ['mapping',          'Field mapping',            null],
        ] as [Tab, string, number | null][]).map(([id, label, count]) => (
          <button key={id} className={`tab${tab === id ? ' active' : ''}`}
            onClick={() => { setTab(id); setSearch('') }}>
            {label}{count !== null && <span className="count">{count}</span>}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'users'            && <UsersTab rows={filteredUsers} search={search} onSearch={setSearch} total={users.length} />}
        {tab === 'customer-mapping' && <CustomerMappingTab />}
        {tab === 'user-mapping'     && <UserMappingTab />}
        {tab === 'price-mapping'    && <PriceMappingTab />}
        {tab === 'time-mapping'     && <TimeMappingTab />}
        {tab === 'log'              && <SyncLogTab log={syncLog} />}
        {tab === 'mapping'          && <MappingTab />}
      </div>
    </div>
  )
}

// ── Users tab ────────────────────────────────────────────────────────────────

function UsersTab({ rows, search, onSearch, total }: {
  rows: TimeTrackerUser[]; search: string; onSearch: (v: string) => void; total: number
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search users…" />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
        <button className="btn btn-ghost btn-sm"><Download size={13} />Export</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Title</th>
            <th>Fortnox employee</th>
            <th>Employment date</th>
            <th style={{ width: 80, textAlign: 'center' }}>Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(u => (
            <tr key={u.id}>
              <td>
                <div className="row" style={{ gap: 8 }}>
                  <PersonChip name={`${u.firstName ?? ''} ${u.lastName ?? ''}`} id={u.id} />
                  <span style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</span>
                </div>
              </td>
              <td className="muted" style={{ fontSize: 12 }}>{u.email ?? '—'}</td>
              <td className="muted">{u.jobTitle ?? '—'}</td>
              <td className="mono muted" style={{ fontSize: 12 }}>{u.fortnoxEmployeeId ?? '—'}</td>
              <td className="muted tabular" style={{ fontSize: 12 }}>{u.employmentDate ? u.employmentDate.slice(0, 10) : '—'}</td>
              <td style={{ textAlign: 'center' }}>
                {u.isActive
                  ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                  : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />
                }
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>
              No users synced yet — click Sync now
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Sync log tab ─────────────────────────────────────────────────────────────

function SyncLogTab({ log }: { log: SyncLogEntry[] }) {
  const [entityFilter, setEntityFilter] = useState('all')
  const entities = Array.from(new Set(log.map(l => l.entity))).sort()
  const filtered  = entityFilter === 'all' ? log : log.filter(l => l.entity === entityFilter)

  const grouped: Record<string, SyncLogEntry[]> = {}
  filtered.forEach(l => {
    const key = new Date(l.syncedAt).toLocaleString('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    })
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(l)
  })

  const cutoff   = Date.now() - 24 * 60 * 60 * 1000
  const last24   = log.filter(l => new Date(l.syncedAt).getTime() > cutoff)
  const successes = last24.filter(l => l.status === 'success').length
  const rate     = last24.length > 0 ? Math.round((successes / last24.length) * 100) : 100

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 6, flexWrap: 'wrap' }}>
          <button className={`chip${entityFilter === 'all' ? ' active' : ''}`} onClick={() => setEntityFilter('all')}>All</button>
          {entities.map(e => (
            <button key={e} className={`chip${entityFilter === e ? ' active' : ''}`}
              onClick={() => setEntityFilter(e)} style={{ textTransform: 'capitalize' }}>{e}</button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm"><Download size={13} />Export log</button>
        </div>
        <div style={{ maxHeight: 540, overflow: 'auto' }}>
          {Object.entries(grouped).map(([minute, items]) => (
            <div key={minute}>
              <div style={{
                padding: '8px 14px', background: 'var(--ns-surface-2)',
                fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--ns-text-muted)', borderTop: '1px solid var(--ns-border)',
                position: 'sticky', top: 0, zIndex: 1,
              }} className="tabular">{minute}</div>
              {items.map(l => (
                <div key={l.id} className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--ns-border)', gap: 12 }}>
                  {l.status === 'success'
                    ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)', flexShrink: 0 }} />
                    : <AlertCircle size={14} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
                  }
                  <span style={{
                    fontSize: 11, padding: '1px 7px', borderRadius: 4,
                    background: 'var(--ns-surface-2)', color: 'var(--ns-text-muted)',
                    border: '1px solid var(--ns-border)', fontWeight: 500,
                    minWidth: 100, textAlign: 'center', flexShrink: 0, textTransform: 'capitalize',
                  }}>{l.entity.replace(/_/g, ' ')}</span>
                  <span style={{ flex: 1, fontSize: 12.5 }}>
                    {l.recordsUpserted > 0
                      ? <>Synced <strong className="tabular">{l.recordsUpserted.toLocaleString()}</strong> records</>
                      : l.errorMessage
                        ? <span style={{ color: 'var(--ns-err)' }}>{l.errorMessage}</span>
                        : <span className="muted">No changes</span>
                    }
                  </span>
                  <span className="mono tabular muted" style={{ fontSize: 11 }}>
                    {new Date(l.syncedAt).toLocaleTimeString('sv-SE')}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: 32 }}>No sync history yet — click Sync now</div>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 14, alignSelf: 'start' }}>
        <div className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Last 24h</div>
        <div className="mono tabular" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
          {successes}<span style={{ fontSize: 13, color: 'var(--ns-text-muted)', fontWeight: 500 }}>/{last24.length || '—'}</span>
        </div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{last24.length - successes} failed · {rate}% success</div>
        <div style={{ marginTop: 16 }}>
          {(['users', 'customers', 'projects', 'articles', 'reported_time', 'reported_time_delta'] as const).map(entity => {
            const hasEntry = log.some(l => l.entity === entity)
            const hasError = log.filter(l => l.entity === entity).some(l => l.status === 'failed')
            if (!hasEntry) return null
            return (
              <div key={entity} className="row" style={{ padding: '5px 0', justifyContent: 'space-between', borderTop: '1px solid var(--ns-border)' }}>
                <span style={{ fontSize: 12, textTransform: 'capitalize' }}>{entity.replace(/_/g, ' ')}</span>
                <span className={`dot ${hasError ? 'dot-warn' : 'dot-ok'}`} style={{ width: 6, height: 6 }} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Customer Project Mapping tab (WI-11777) ──────────────────────────────────
//
// Two buckets:
//   1. "Mapping available" — projects with a TimeTracker-derived suggestion
//      that resolves to a Forge customer. User can search, multi-select with
//      checkboxes, then "Map selected" or "Map all" / "Map filtered".
//      Already-correct rows are dimmed and not selectable; applying the bulk
//      mutation only affects rows where the customer would actually change.
//   2. "Mapping unavailable" — projects that can't be mapped, with a reason
//      chip explaining why (no Fortnox #, TimeTracker has no project / no
//      customer link / customer not in Forge).

const REASON_LABEL: Record<MappingUnavailableReason, string> = {
  no_fortnox_project_number:      'No Fortnox project number on Forge project',
  no_tt_project:                  'Not in TimeTracker',
  tt_project_has_no_customer:     'TimeTracker project has no customer',
  tt_customer_missing_fortnox_id: 'TimeTracker customer missing Fortnox id',
  fortnox_customer_not_in_forge:  'Fortnox customer not in Forge',
}

function CustomerMappingTab() {
  const queryClient = useQueryClient()
  const [search,   setSearch]   = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const { data, isLoading, isError } = useQuery({
    queryKey: ['timetracker', 'customer-mapping'],
    queryFn:  () => api.get<CustomerProjectMappingResponse>('/timetracker/customer-mapping'),
  })

  const apply = useMutation({
    mutationFn: (projectIds: number[]) =>
      api.post<{ applied: number; requested: number }>(
        '/timetracker/customer-mapping/apply', { projectIds },
      ),
    onSuccess: () => {
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['timetracker', 'customer-mapping'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  // All hooks must be called before any early return — keep this above the
  // loading / error guards or React throws "Rendered more hooks than during
  // the previous render" when data flips from undefined to populated.
  const available   = data?.mappingAvailable   ?? []
  const unavailable = data?.mappingUnavailable ?? []

  // Only rows whose customer would actually change can be applied.
  const changeableIds = available.filter(r => !r.alreadyMatches).map(r => r.forgeProjectId)

  const q = search.toLowerCase().trim()
  const filtered = q
    ? available.filter(r =>
        r.forgeProjectName.toLowerCase().includes(q) ||
        (r.fortnoxProjectNumber ?? '').toLowerCase().includes(q) ||
        (r.suggestedCustomerName ?? '').toLowerCase().includes(q) ||
        (r.currentCustomerName ?? '').toLowerCase().includes(q))
    : available

  const filteredChangeableIds = filtered.filter(r => !r.alreadyMatches).map(r => r.forgeProjectId)

  const filteredSelected = filteredChangeableIds.filter(id => selected.has(id))
  const allFilteredSelected = filteredChangeableIds.length > 0 && filteredSelected.length === filteredChangeableIds.length
  const someFilteredSelected = filteredSelected.length > 0 && !allFilteredSelected

  const headerCheckRef = useHeaderCheckbox(someFilteredSelected)

  if (isLoading) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Loading…</p>
  if (isError || !data) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Failed to load mapping.</p>

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) filteredChangeableIds.forEach(id => next.delete(id))
      else                     filteredChangeableIds.forEach(id => next.add(id))
      return next
    })
  }

  const mapAll      = () => apply.mutate(changeableIds)
  const mapFiltered = () => apply.mutate(filteredChangeableIds)
  const mapSelected = () => apply.mutate([...selected])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Mapping available */}
      <section>
        <header className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping available</h2>
            <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {available.length === 0
                ? 'No projects with a TimeTracker-derived customer suggestion.'
                : `${changeableIds.length} project${changeableIds.length === 1 ? '' : 's'} can be updated · ${available.length - changeableIds.length} already match`}
            </p>
          </div>
        </header>

        <div className="card" style={{ padding: 0 }}>
          {/* Toolbar */}
          <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8, flexWrap: 'wrap' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search project, Fortnox #, customer…" />
            <span className="muted tabular" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              {filtered.length} of {available.length}
            </span>
            <div style={{ flex: 1 }} />
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: 12.5, alignSelf: 'center' }}>{selected.size} selected</span>
                <button className="btn btn-ghost" onClick={mapSelected} disabled={apply.isPending}>
                  Map selected
                </button>
              </>
            )}
            {q && filteredChangeableIds.length > 0 && (
              <button className="btn btn-ghost" onClick={mapFiltered} disabled={apply.isPending}>
                Map filtered ({filteredChangeableIds.length})
              </button>
            )}
            <button
              className="btn btn-brand"
              onClick={mapAll}
              disabled={apply.isPending || changeableIds.length === 0}
            >
              {apply.isPending ? 'Mapping…' : `Map all (${changeableIds.length})`}
            </button>
          </div>

          {available.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              Nothing to map.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      disabled={filteredChangeableIds.length === 0}
                      style={{ cursor: filteredChangeableIds.length === 0 ? 'default' : 'pointer' }}
                    />
                  </th>
                  <th>Project</th>
                  <th style={{ width: 130 }}>Fortnox #</th>
                  <th>Current customer</th>
                  <th>Suggested</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr
                    key={r.forgeProjectId}
                    onClick={() => !r.alreadyMatches && toggle(r.forgeProjectId)}
                    style={{
                      cursor:  r.alreadyMatches ? 'default' : 'pointer',
                      opacity: r.alreadyMatches ? 0.55 : 1,
                      background: !r.alreadyMatches && selected.has(r.forgeProjectId) ? 'var(--ns-brand-soft)' : undefined,
                    }}
                  >
                    <td onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.forgeProjectId)}
                        onChange={() => toggle(r.forgeProjectId)}
                        disabled={r.alreadyMatches}
                        style={{ cursor: r.alreadyMatches ? 'default' : 'pointer' }}
                      />
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {r.forgeProjectName}
                      {r.alreadyMatches && (
                        <span className="badge badge-ok" style={{ marginLeft: 8, fontSize: 10 }}>Already mapped</span>
                      )}
                    </td>
                    <td>
                      {r.fortnoxProjectNumber
                        ? <span className="row" style={{ gap: 5 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 12 }}>{r.fortnoxProjectNumber}</span>
                          </span>
                        : <span className="muted" style={{ fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td className="muted">
                      {r.currentCustomerName ?? <span style={{ fontStyle: 'italic' }}>None</span>}
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{r.suggestedCustomerName}</span>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    No projects match the current search.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Mapping unavailable */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping unavailable</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {unavailable.length} project{unavailable.length === 1 ? '' : 's'} that TimeTracker can't auto-map. Map them manually in the project drawer.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          {unavailable.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              All non-archived projects have a TimeTracker mapping.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th style={{ width: 130 }}>Fortnox #</th>
                  <th>Current customer</th>
                  <th style={{ width: 280 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {unavailable.map(r => (
                  <tr key={r.forgeProjectId}>
                    <td style={{ fontWeight: 500 }}>{r.forgeProjectName}</td>
                    <td>
                      {r.fortnoxProjectNumber
                        ? <span className="row" style={{ gap: 5 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 12 }}>{r.fortnoxProjectNumber}</span>
                          </span>
                        : <span className="muted" style={{ fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td className="muted">
                      {r.currentCustomerName ?? <span style={{ fontStyle: 'italic' }}>None</span>}
                    </td>
                    <td>
                      <span className="badge" style={{ fontSize: 11 }}>{REASON_LABEL[r.reason]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}

// Header checkbox with indeterminate support — referenced by CustomerMappingTab.
function useHeaderCheckbox(indeterminate: boolean) {
  const ref = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return ref
}

// ── User Project Mapping tab (WI-11777, second part) ──────────────────────────
//
// Walks tt.projects → reported_time → tt.users to find a distinct list of users
// per project. Each TimeTracker user is resolved to a Forge person by email.
// "Map" adds the person as a phase resource on every active phase of the
// project, plus to each phase's default activity.

const USER_REASON_LABEL: Record<UserMappingUnavailableReason, string> = {
  no_active_phases:    'Project has no active phases',
  no_resolvable_users: 'No TimeTracker user resolves to a Forge person',
  unknown:             'Unknown',
}

function pairKey(projectId: number, personId: number): string {
  return `${projectId}::${personId}`
}

function UserMappingTab() {
  const queryClient = useQueryClient()
  const [search,   setSearch]   = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data, isLoading, isError } = useQuery({
    queryKey: ['timetracker', 'user-mapping'],
    queryFn:  () => api.get<UserProjectMappingResponse>('/timetracker/user-mapping'),
  })

  const apply = useMutation({
    mutationFn: (pairs: { projectId: number; personId: number }[]) =>
      api.post<{ phaseResourcesAdded: number; activityResourcesAdded: number; pairsRequested: number }>(
        '/timetracker/user-mapping/apply', { pairs },
      ),
    onSuccess: () => {
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['timetracker', 'user-mapping'] })
      queryClient.invalidateQueries({ queryKey: ['phases'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  // All hooks (incl. useHeaderCheckbox below) must run before the early
  // returns to keep React's hook order stable across renders.
  const available   = data?.mappingAvailable   ?? []
  const unavailable = data?.mappingUnavailable ?? []

  // Pairs that still need work (not already on every phase).
  const changeable = available.filter(r => !r.alreadyOnAllPhases)
  const changeableKeys = changeable.map(r => pairKey(r.forgeProjectId, r.personId))

  const q = search.toLowerCase().trim()
  const filtered = q
    ? available.filter(r =>
        r.forgeProjectName.toLowerCase().includes(q) ||
        r.personName.toLowerCase().includes(q) ||
        (r.personEmail ?? '').toLowerCase().includes(q) ||
        (r.fortnoxProjectNumber ?? '').toLowerCase().includes(q))
    : available

  const filteredChangeable = filtered.filter(r => !r.alreadyOnAllPhases)
  const filteredChangeableKeys = filteredChangeable.map(r => pairKey(r.forgeProjectId, r.personId))

  const filteredSelected     = filteredChangeableKeys.filter(k => selected.has(k))
  const allFilteredSelected  = filteredChangeable.length > 0 && filteredSelected.length === filteredChangeable.length
  const someFilteredSelected = filteredSelected.length > 0 && !allFilteredSelected
  const headerCheckRef = useHeaderCheckbox(someFilteredSelected)

  if (isLoading) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Loading…</p>
  if (isError || !data) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Failed to load mapping.</p>

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }
  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) filteredChangeableKeys.forEach(k => next.delete(k))
      else                     filteredChangeableKeys.forEach(k => next.add(k))
      return next
    })
  }

  const keysToPairs = (keys: string[]): { projectId: number; personId: number }[] =>
    keys.map(k => {
      const [pid, perid] = k.split('::')
      return { projectId: Number(pid), personId: Number(perid) }
    })

  const mapAll      = () => apply.mutate(keysToPairs(changeableKeys))
  const mapFiltered = () => apply.mutate(keysToPairs(filteredChangeableKeys))
  const mapSelected = () => apply.mutate(keysToPairs([...selected]))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Mapping available */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping available</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {available.length === 0
              ? 'No (project, person) pairs found from TimeTracker reported time.'
              : `${changeable.length} pair${changeable.length === 1 ? '' : 's'} can be mapped · ${available.length - changeable.length} already on every phase`}
            . Mapping adds the person as a phase resource on every active phase of the project, plus to each phase's default activity.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8, flexWrap: 'wrap' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search project, person, email…" />
            <span className="muted tabular" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              {filtered.length} of {available.length}
            </span>
            <div style={{ flex: 1 }} />
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: 12.5, alignSelf: 'center' }}>{selected.size} selected</span>
                <button className="btn btn-ghost" onClick={mapSelected} disabled={apply.isPending}>
                  Map selected
                </button>
              </>
            )}
            {q && filteredChangeableKeys.length > 0 && (
              <button className="btn btn-ghost" onClick={mapFiltered} disabled={apply.isPending}>
                Map filtered ({filteredChangeableKeys.length})
              </button>
            )}
            <button
              className="btn btn-brand"
              onClick={mapAll}
              disabled={apply.isPending || changeableKeys.length === 0}
            >
              {apply.isPending ? 'Mapping…' : `Map all (${changeableKeys.length})`}
            </button>
          </div>

          {available.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              Nothing to map.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      disabled={filteredChangeable.length === 0}
                      style={{ cursor: filteredChangeable.length === 0 ? 'default' : 'pointer' }}
                    />
                  </th>
                  <th>Project</th>
                  <th>Person</th>
                  <th style={{ width: 100, textAlign: 'right' }}>TT hours</th>
                  <th style={{ width: 130 }}>Phases</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const key = pairKey(r.forgeProjectId, r.personId)
                  return (
                    <tr
                      key={key}
                      onClick={() => !r.alreadyOnAllPhases && toggle(key)}
                      style={{
                        cursor:  r.alreadyOnAllPhases ? 'default' : 'pointer',
                        opacity: r.alreadyOnAllPhases ? 0.55 : 1,
                        background: !r.alreadyOnAllPhases && selected.has(key) ? 'var(--ns-brand-soft)' : undefined,
                      }}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggle(key)}
                          disabled={r.alreadyOnAllPhases}
                          style={{ cursor: r.alreadyOnAllPhases ? 'default' : 'pointer' }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.forgeProjectName}</div>
                        {r.fortnoxProjectNumber && (
                          <div className="row" style={{ gap: 5, marginTop: 2 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 11 }}>{r.fortnoxProjectNumber}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.personName}</div>
                        {r.personEmail && (
                          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{r.personEmail}</div>
                        )}
                      </td>
                      <td className="mono tabular" style={{ textAlign: 'right' }}>
                        {r.ttHours.toLocaleString('sv-SE', { maximumFractionDigits: 1 })}h
                      </td>
                      <td>
                        {r.alreadyOnAllPhases ? (
                          <span className="badge badge-ok" style={{ fontSize: 10 }}>
                            On all {r.totalActivePhases}
                          </span>
                        ) : (
                          <span className="muted" style={{ fontSize: 12 }}>
                            {r.phasesAssigned} of {r.totalActivePhases}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    No pairs match the current search.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Mapping unavailable */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping unavailable</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {unavailable.length} project{unavailable.length === 1 ? '' : 's'} where TimeTracker has reported time but auto-mapping isn't possible.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          {unavailable.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              Every project with TimeTracker time can be mapped.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th style={{ width: 130 }}>Fortnox #</th>
                  <th style={{ width: 90, textAlign: 'right' }}>TT users</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Resolved</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Active phases</th>
                  <th style={{ width: 280 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {unavailable.map(r => (
                  <tr key={r.forgeProjectId}>
                    <td style={{ fontWeight: 500 }}>{r.forgeProjectName}</td>
                    <td>
                      {r.fortnoxProjectNumber
                        ? <span className="row" style={{ gap: 5 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 12 }}>{r.fortnoxProjectNumber}</span>
                          </span>
                        : <span className="muted" style={{ fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{r.ttUserCount}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{r.resolvedUserCount}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{r.activePhaseCount}</td>
                    <td>
                      <span className="badge" style={{ fontSize: 11 }}>{USER_REASON_LABEL[r.reason]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}

// ── Resource Pricing tab (WI-11778) ───────────────────────────────────────────
//
// Imports per-(project, person, article) prices from TimeTracker into Forge's
// activity_rates. Each row becomes an activity-level rate scoped to one
// person, with valid_from / valid_to derived from the actual reported_time
// date range. Articles materialise as activities named "Article {id}" under
// the project's default phase — purely a migration label so prices line up
// 1:1 with TimeTracker. Admins can rename activities afterwards.

const PRICE_REASON_LABEL: Record<PriceMappingUnavailableReason, string> = {
  forge_project_not_found: 'Forge project not found (no Fortnox # match)',
  forge_person_not_found:  'Forge person not found (email mismatch)',
  no_default_phase:        'Project has no default active phase',
  unknown:                 'Unknown',
}

function priceKey(r: { forgeProjectId: number; personId: number; articleId: number }): string {
  return `${r.forgeProjectId}::${r.personId}::${r.articleId}`
}

function PriceMappingTab() {
  const queryClient = useQueryClient()
  const [search,        setSearch]        = useState('')
  const [selected,      setSelected]      = useState<Set<string>>(new Set())
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['timetracker', 'price-mapping'],
    queryFn:  () => api.get<ResourcePriceMappingResponse>('/timetracker/price-mapping'),
  })

  const apply = useMutation({
    mutationFn: (keys: { projectId: number; personId: number; articleId: number }[]) =>
      api.post<{
        activitiesCreated: number
        phaseResourcesAdded: number
        activityResourcesAdded: number
        ratesInserted: number
        skippedAlreadyImported: number
        skippedUnresolvable: number
        requested: number
      }>('/timetracker/price-mapping/apply', { keys }),
  })

  const runImport = async (keys: { projectId: number; personId: number; articleId: number }[]) => {
    const chunks: typeof keys[] = []
    for (let i = 0; i < keys.length; i += IMPORT_BATCH_SIZE)
      chunks.push(keys.slice(i, i + IMPORT_BATCH_SIZE))

    setBatchProgress({ done: 0, total: keys.length })
    let done = 0
    try {
      for (const chunk of chunks) {
        await apply.mutateAsync(chunk)
        done += chunk.length
        setBatchProgress({ done, total: keys.length })
      }
    } finally {
      setBatchProgress(null)
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['timetracker', 'price-mapping'] })
      queryClient.invalidateQueries({ queryKey: ['phases'] })
      queryClient.invalidateQueries({ queryKey: ['rates'] })
    }
  }

  // All hooks above the early returns to keep React's hook order stable.
  const available   = data?.mappingAvailable   ?? []
  const unavailable = data?.mappingUnavailable ?? []

  // Only rows that aren't already imported can be applied.
  const importable     = available.filter(r => !r.alreadyImported)
  const importableKeys = importable.map(r => priceKey(r))

  const q = search.toLowerCase().trim()
  const filtered = q
    ? available.filter(r =>
        r.forgeProjectName.toLowerCase().includes(q) ||
        r.personName.toLowerCase().includes(q) ||
        (r.personEmail ?? '').toLowerCase().includes(q) ||
        (r.fortnoxProjectCode ?? '').toLowerCase().includes(q) ||
        (r.articleCode ?? '').toLowerCase().includes(q) ||
        (r.articleName ?? '').toLowerCase().includes(q) ||
        String(r.articleId).includes(q))
    : available

  const filteredImportable     = filtered.filter(r => !r.alreadyImported)
  const filteredImportableKeys = filteredImportable.map(r => priceKey(r))

  const filteredSelected     = filteredImportableKeys.filter(k => selected.has(k))
  const allFilteredSelected  = filteredImportable.length > 0 && filteredSelected.length === filteredImportable.length
  const someFilteredSelected = filteredSelected.length > 0 && !allFilteredSelected
  const headerCheckRef = useHeaderCheckbox(someFilteredSelected)

  if (isLoading) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Loading…</p>
  if (isError || !data) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Failed to load mapping.</p>

  const toggle = (k: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  }
  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) filteredImportableKeys.forEach(k => next.delete(k))
      else                     filteredImportableKeys.forEach(k => next.add(k))
      return next
    })
  }

  const keysToBody = (rows: PriceMappingAvailableRow[]) =>
    rows.map(r => ({ projectId: r.forgeProjectId, personId: r.personId, articleId: r.articleId }))

  const isImporting    = apply.isPending || batchProgress !== null
  const importAll      = () => runImport(keysToBody(importable))
  const importFiltered = () => runImport(keysToBody(filteredImportable))
  const importSelected = () => runImport(keysToBody(available.filter(r => selected.has(priceKey(r)))))

  const fmtPrice = (p: number | null | undefined) =>
    p == null ? '—' : `${p.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr/h`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Mapping available */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping available</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {available.length === 0
              ? 'No (project, person, article) price rows from TimeTracker.'
              : `${importable.length} row${importable.length === 1 ? '' : 's'} can be imported · ${available.length - importable.length} already imported`}
            . Importing creates an activity called "Article {'{id}'}" under the project's default phase, assigns the person, and sets an activity-rate with the price and date range.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8, flexWrap: 'wrap' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search project, person, article…" />
            <span className="muted tabular" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              {filtered.length} of {available.length}
            </span>
            <div style={{ flex: 1 }} />
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: 12.5, alignSelf: 'center' }}>{selected.size} selected</span>
                <button className="btn btn-ghost" onClick={importSelected} disabled={isImporting}>
                  Import selected
                </button>
              </>
            )}
            {q && filteredImportableKeys.length > 0 && (
              <button className="btn btn-ghost" onClick={importFiltered} disabled={isImporting}>
                Import filtered ({filteredImportableKeys.length})
              </button>
            )}
            <button
              className="btn btn-brand"
              onClick={importAll}
              disabled={isImporting || importableKeys.length === 0}
            >
              {batchProgress
                ? `Importing… ${batchProgress.done}/${batchProgress.total}`
                : `Import all (${importableKeys.length})`}
            </button>
          </div>

          {available.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              Nothing to import.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      disabled={filteredImportable.length === 0}
                      style={{ cursor: filteredImportable.length === 0 ? 'default' : 'pointer' }}
                    />
                  </th>
                  <th>Project</th>
                  <th>Person</th>
                  <th>Article</th>
                  <th style={{ width: 130, textAlign: 'right' }}>Price</th>
                  <th style={{ width: 200 }}>Valid range</th>
                  <th style={{ width: 100, textAlign: 'right' }}>TT hours</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const k = priceKey(r)
                  const dim = r.alreadyImported
                  return (
                    <tr
                      key={k}
                      onClick={() => !dim && toggle(k)}
                      style={{
                        cursor:  dim ? 'default' : 'pointer',
                        opacity: dim ? 0.55 : 1,
                        background: !dim && selected.has(k) ? 'var(--ns-brand-soft)' : undefined,
                      }}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(k)}
                          onChange={() => toggle(k)}
                          disabled={dim}
                          style={{ cursor: dim ? 'default' : 'pointer' }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.forgeProjectName}</div>
                        {r.fortnoxProjectCode && (
                          <div className="row" style={{ gap: 5, marginTop: 2 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 11 }}>{r.fortnoxProjectCode}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.personName}</div>
                        {r.personEmail && (
                          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{r.personEmail}</div>
                        )}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6 }}>
                          <span className="badge" style={{ fontSize: 11 }}>#{r.articleId}</span>
                          <span style={{ fontSize: 12.5 }}>
                            {r.articleName ?? r.articleCode ?? <span className="muted">—</span>}
                          </span>
                        </div>
                        {r.alreadyImported && (
                          <span className="badge badge-ok" style={{ fontSize: 10, marginTop: 2 }}>Already imported</span>
                        )}
                      </td>
                      <td className="mono tabular" style={{ textAlign: 'right' }}>
                        {fmtPrice(r.pricePerHour)}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {r.validFrom ?? '—'} → {r.validTo ?? '—'}
                      </td>
                      <td className="mono tabular" style={{ textAlign: 'right' }}>
                        {(r.billableHours ?? 0).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}h
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    No rows match the current search.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Mapping unavailable */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping unavailable</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {unavailable.length} (project, person) pair{unavailable.length === 1 ? '' : 's'} where TimeTracker has priced reported time but auto-import isn't possible.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          {unavailable.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              All TimeTracker price rows resolved successfully.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Person</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Articles</th>
                  <th style={{ width: 110, textAlign: 'right' }}>TT hours</th>
                  <th style={{ width: 320 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {unavailable.map((r, i) => (
                  <tr key={`${r.fortnoxProjectCode}-${r.personEmail}-${i}`}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {r.forgeProjectName ?? <span className="muted" style={{ fontStyle: 'italic' }}>—</span>}
                      </div>
                      {r.fortnoxProjectCode && (
                        <div className="row" style={{ gap: 5, marginTop: 2 }}>
                          <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                          <span className="mono muted" style={{ fontSize: 11 }}>{r.fortnoxProjectCode}</span>
                        </div>
                      )}
                    </td>
                    <td className="muted">{r.personEmail ?? '—'}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{r.articleCount}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>
                      {(r.billableHours ?? 0).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}h
                    </td>
                    <td>
                      <span className="badge" style={{ fontSize: 11 }}>{PRICE_REASON_LABEL[r.reason]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}

// ── Registered time mapping tab (WI-11808) ────────────────────────────────────
// One row per (project, person) bucket aggregating TimeTracker reported_time.
// Each TT row materialises as a Forge time.entry with the source row's id
// stamped onto tt_reported_time_id, so re-imports are no-ops. Article rows
// must have a matching activity (created by the Resource Pricing tab) — buckets
// with missing activities are flagged and skipped at apply time.

const TIME_REASON_LABEL: Record<TimeMappingUnavailableReason, string> = {
  no_fortnox_code:         'TimeTracker project has no Fortnox code',
  forge_project_not_found: 'Forge project not found (no Fortnox # match)',
  forge_person_not_found:  'Forge person not found (email mismatch)',
  no_forge_user:           'User has not logged into Forge yet',
  no_default_phase:        'Project has no default active phase',
  unknown:                 'Unknown',
}

function timeKey(r: { forgeProjectId: number; personId: number }): string {
  return `${r.forgeProjectId}::${r.personId}`
}

const IMPORT_BATCH_SIZE = 50

function TimeMappingTab() {
  const queryClient = useQueryClient()
  const [search,        setSearch]        = useState('')
  const [selected,      setSelected]      = useState<Set<string>>(new Set())
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['timetracker', 'time-mapping'],
    queryFn:  () => api.get<TimeMappingResponse>('/timetracker/time-mapping'),
  })

  const apply = useMutation({
    mutationFn: (pairs: { projectId: number; personId: number }[]) =>
      api.post<{
        entriesInserted: number
        skippedAlreadyImported: number
        skippedNoActivity: number
        skippedUnresolvable: number
        requestedPairs: number
      }>('/timetracker/time-mapping/apply', { pairs }),
  })

  const runImport = async (pairs: { projectId: number; personId: number }[]) => {
    const chunks: typeof pairs[] = []
    for (let i = 0; i < pairs.length; i += IMPORT_BATCH_SIZE)
      chunks.push(pairs.slice(i, i + IMPORT_BATCH_SIZE))

    setBatchProgress({ done: 0, total: pairs.length })
    let done = 0
    try {
      for (const chunk of chunks) {
        await apply.mutateAsync(chunk)
        done += chunk.length
        setBatchProgress({ done, total: pairs.length })
      }
    } finally {
      setBatchProgress(null)
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['timetracker', 'time-mapping'] })
      queryClient.invalidateQueries({ queryKey: ['time'] })
    }
  }

  // All hooks above the early returns to keep React's hook order stable.
  const available   = data?.mappingAvailable   ?? []
  const unavailable = data?.mappingUnavailable ?? []

  // A bucket is importable iff it has at least one entry that's not yet
  // imported AND has a resolved activity. entriesPending captures exactly that.
  const importable     = available.filter(r => r.entriesPending > 0)
  const importableKeys = importable.map(r => timeKey(r))

  const q = search.toLowerCase().trim()
  const filtered = q
    ? available.filter(r =>
        r.forgeProjectName.toLowerCase().includes(q) ||
        r.personName.toLowerCase().includes(q) ||
        (r.personEmail ?? '').toLowerCase().includes(q) ||
        (r.fortnoxProjectCode ?? '').toLowerCase().includes(q))
    : available

  const filteredImportable     = filtered.filter(r => r.entriesPending > 0)
  const filteredImportableKeys = filteredImportable.map(r => timeKey(r))

  const filteredSelected     = filteredImportableKeys.filter(k => selected.has(k))
  const allFilteredSelected  = filteredImportable.length > 0 && filteredSelected.length === filteredImportable.length
  const someFilteredSelected = filteredSelected.length > 0 && !allFilteredSelected
  const headerCheckRef = useHeaderCheckbox(someFilteredSelected)

  if (isLoading) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Loading…</p>
  if (isError || !data) return <p className="muted" style={{ paddingTop: 24, textAlign: 'center' }}>Failed to load mapping.</p>

  const toggle = (k: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  }
  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) filteredImportableKeys.forEach(k => next.delete(k))
      else                     filteredImportableKeys.forEach(k => next.add(k))
      return next
    })
  }

  const pairsBody = (rows: TimeMappingAvailableRow[]) =>
    rows.map(r => ({ projectId: r.forgeProjectId, personId: r.personId }))

  const isImporting    = apply.isPending || batchProgress !== null
  const importAll      = () => runImport(pairsBody(importable))
  const importFiltered = () => runImport(pairsBody(filteredImportable))
  const importSelected = () => runImport(pairsBody(available.filter(r => selected.has(timeKey(r)))))

  const fmtHours = (h: number | null | undefined) =>
    h == null ? '—' : `${h.toLocaleString('sv-SE', { maximumFractionDigits: 1 })}h`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Mapping available */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping available</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {available.length === 0
              ? 'No (project, person) buckets from TimeTracker.'
              : `${importable.length} bucket${importable.length === 1 ? '' : 's'} have pending entries · ${available.length - importable.length} fully imported`}
            . Importing creates one Forge time entry per TimeTracker row, mapped to the project's default phase and the article-named activity (created by Resource Pricing). Re-running is a no-op.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          <div className="row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8, flexWrap: 'wrap' }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search project or person…" />
            <span className="muted tabular" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              {filtered.length} of {available.length}
            </span>
            <div style={{ flex: 1 }} />
            {selected.size > 0 && (
              <>
                <span style={{ fontSize: 12.5, alignSelf: 'center' }}>{selected.size} selected</span>
                <button className="btn btn-ghost" onClick={importSelected} disabled={isImporting}>
                  Import selected
                </button>
              </>
            )}
            {q && filteredImportableKeys.length > 0 && (
              <button className="btn btn-ghost" onClick={importFiltered} disabled={isImporting}>
                Import filtered ({filteredImportableKeys.length})
              </button>
            )}
            <button
              className="btn btn-brand"
              onClick={importAll}
              disabled={isImporting || importableKeys.length === 0}
            >
              {batchProgress
                ? `Importing… ${batchProgress.done}/${batchProgress.total}`
                : `Import all (${importableKeys.length})`}
            </button>
          </div>

          {available.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              Nothing to import.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      disabled={filteredImportable.length === 0}
                      style={{ cursor: filteredImportable.length === 0 ? 'default' : 'pointer' }}
                    />
                  </th>
                  <th>Project</th>
                  <th>Person</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Pending</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Imported</th>
                  <th style={{ width: 130, textAlign: 'right' }}>Missing activity</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Pending hrs</th>
                  <th style={{ width: 200 }}>Date range</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const k = timeKey(r)
                  const dim = r.entriesPending === 0
                  return (
                    <tr
                      key={k}
                      onClick={() => !dim && toggle(k)}
                      style={{
                        cursor:  dim ? 'default' : 'pointer',
                        opacity: dim ? 0.55 : 1,
                        background: !dim && selected.has(k) ? 'var(--ns-brand-soft)' : undefined,
                      }}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(k)}
                          onChange={() => toggle(k)}
                          disabled={dim}
                          style={{ cursor: dim ? 'default' : 'pointer' }}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.forgeProjectName}</div>
                        {r.fortnoxProjectCode && (
                          <div className="row" style={{ gap: 5, marginTop: 2 }}>
                            <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                            <span className="mono muted" style={{ fontSize: 11 }}>{r.fortnoxProjectCode}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.personName}</div>
                        {r.personEmail && (
                          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{r.personEmail}</div>
                        )}
                        {r.entriesPending === 0 && r.entriesImported > 0 && (
                          <span className="badge badge-ok" style={{ fontSize: 10, marginTop: 2 }}>Fully imported</span>
                        )}
                      </td>
                      <td className="mono tabular" style={{ textAlign: 'right' }}>{r.entriesPending}</td>
                      <td className="mono tabular muted" style={{ textAlign: 'right' }}>{r.entriesImported}</td>
                      <td className="mono tabular" style={{ textAlign: 'right', color: r.entriesMissingActivity > 0 ? 'var(--ns-warn)' : undefined }}>
                        {r.entriesMissingActivity}
                      </td>
                      <td className="mono tabular" style={{ textAlign: 'right' }}>{fmtHours(r.hoursPending)}</td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {r.dateFrom ?? '—'} → {r.dateTo ?? '—'}
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    No rows match the current search.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Mapping unavailable */}
      <section>
        <header style={{ marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Mapping unavailable</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {unavailable.length} (project, person) pair{unavailable.length === 1 ? '' : 's'} where TimeTracker has reported time but auto-import isn't possible.
          </p>
        </header>

        <div className="card" style={{ padding: 0 }}>
          {unavailable.length === 0 ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
              All TimeTracker reported time resolved successfully.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Person</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Entries</th>
                  <th style={{ width: 110, textAlign: 'right' }}>TT hours</th>
                  <th style={{ width: 200 }}>Date range</th>
                  <th style={{ width: 280 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {unavailable.map((r, i) => (
                  <tr key={`${r.fortnoxProjectCode}-${r.personEmail}-${i}`}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {r.forgeProjectName ?? <span className="muted" style={{ fontStyle: 'italic' }}>—</span>}
                      </div>
                      {r.fortnoxProjectCode && (
                        <div className="row" style={{ gap: 5, marginTop: 2 }}>
                          <Link2 size={11} style={{ color: 'var(--ns-text-subtle)' }} />
                          <span className="mono muted" style={{ fontSize: 11 }}>{r.fortnoxProjectCode}</span>
                        </div>
                      )}
                    </td>
                    <td className="muted">{r.personEmail ?? '—'}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{r.entriesTotal}</td>
                    <td className="mono tabular" style={{ textAlign: 'right' }}>{fmtHours(r.hoursTotal)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.dateFrom ?? '—'} → {r.dateTo ?? '—'}
                    </td>
                    <td>
                      <span className="badge" style={{ fontSize: 11 }}>{TIME_REASON_LABEL[r.reason]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}

// ── Field mapping tab ─────────────────────────────────────────────────────────

function MappingTab() {
  const sections: { title: string; rows: [string, string][] }[] = [
    {
      title: 'Users (trainee.User → timetracker.users)',
      rows: [
        ['Id',               'id'],
        ['FirstName',        'first_name'],
        ['LastName',         'last_name'],
        ['Email',            'email'],
        ['JobTitle',         'job_title'],
        ['IsActive',         'is_active'],
        ['FortnoxEmployeeId','fortnox_employee_id'],
        ['EmploymentDate',   'employment_date'],
      ],
    },
    {
      title: 'Reported time (trainee.ReportedTime → timetracker.reported_time)',
      rows: [
        ['Id',                  'id'],
        ['EmployeeId',          'employee_id'],
        ['ProjectId',           'project_id'],
        ['ArticleId',           'article_id'],
        ['Date',                'date'],
        ['StartTime / EndTime', 'start_time / end_time'],
        ['WorkedHours',         'worked_hours'],
        ['BillableHours',       'billable_hours'],
        ['Note',                'note'],
        ['FortnoxInvoiceNumber','fortnox_invoice_no'],
        ['LastModifiedTime',    'tt_modified_at (watermark)'],
      ],
    },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(s => (
        <div key={s.title} className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{s.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr', gap: '8px 12px', alignItems: 'center', maxWidth: 600 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>TimeTracker field</div>
            <div />
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Forge column</div>
            {s.rows.map(([from, to], i) => (
              <React.Fragment key={i}>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-surface-2)', borderRadius: 6, border: '1px solid var(--ns-border)' }}>{from}</div>
                <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 14 }}>→</div>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-brand-soft)', borderRadius: 6, color: 'var(--ns-brand-strong)' }}>{to}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>TimeTracker</span>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ns-text-muted)', marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  )
}

function StatCard({ label, value, sub, isCustom }: {
  label: string; value?: React.ReactNode; sub?: string; isCustom?: boolean
}) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 6 }}>{label}</div>
      {isCustom
        ? <div style={{ marginTop: 2 }}>{value}</div>
        : <div className="mono tabular" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
      }
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

function PersonChip({ name, id }: { name: string; id: number }) {
  const initials = name.trim().split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  const hue = (id * 83) % 360
  return (
    <div style={{
      width: 22, height: 22, borderRadius: 5, flexShrink: 0,
      background: `oklch(${0.55 + (id % 7) * 0.03} 0.12 ${hue})`,
      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9.5, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
    }}>{initials || '?'}</div>
  )
}

function SearchInput({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 30, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', width: 260 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ns-text-subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }} />
    </div>
  )
}
